/**
 * IMPORTANT: When adding a new background sound, upload an MP3 preview of the audio asset to the below S3 bucket for preview playback
 */
const S3_BACKGROUND_SOUND_BASE =
  "https://coval-background-sound-previews.s3.us-east-2.amazonaws.com"

interface BackgroundNoiseType {
  id: string
  name: string
  previewUrl?: string
  defaultVolume?: number
}

/**
 * Background noise types with per-sound default volumes.
 * Default volumes are tuned based on the loudness of each audio file.
 * User-specified volumes will override these defaults.
 */
export const BACKGROUND_NOISE_TYPES: BackgroundNoiseType[] = [
  { id: "off", name: "Off", defaultVolume: 1.0 },
  {
    id: "office",
    name: "Office",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/office-ambience.mp3`,
    defaultVolume: 0.4,
  },
  {
    id: "lounge",
    name: "Lounge",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/people-in-lounge.mp3`,
    defaultVolume: 0.3,
  },
  {
    id: "crowd",
    name: "Crowd Talking",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/crowd-talking.mp3`,
    defaultVolume: 0.1,
  },
  {
    id: "airport",
    name: "Airport Boarding",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/boarding-announcement.mp3`,
    defaultVolume: 0.2,
  },
  {
    id: "bus",
    name: "Bus Interior",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/bus-interior.mp3`,
    defaultVolume: 0.3,
  },
  {
    id: "playground",
    name: "Kids Playing",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/kids-playing.mp3`,
    defaultVolume: 0.1,
  },
  {
    id: "doorbell",
    name: "Doorbell",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/doorbell.mp3`,
    defaultVolume: 0.3,
  },
  {
    id: "train-arrival",
    name: "Train Arrival",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/train-arrival.mp3`,
    defaultVolume: 0.2,
  },
  {
    id: "portable-air-conditioner",
    name: "Portable AC",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/portable-air-conditioner.mp3`,
    defaultVolume: 0.18,
  },
  {
    id: "skatepark",
    name: "Skatepark",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/skatepark.mp3`,
    defaultVolume: 0.2,
  },
  {
    id: "small-dog-bark",
    name: "Small Dog Bark",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/small-dog-bark.mp3`,
    defaultVolume: 0.2,
  },
  {
    id: "cafe",
    name: "Cafe",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/cafe.mp3`,
    defaultVolume: 0.5,
  },
  {
    id: "ferry-and-announcement",
    name: "Ferry Announcement",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/ferry-and-announcement.mp3`,
    defaultVolume: 0.2,
  },
  {
    id: "heavy-rain",
    name: "Heavy Rain",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/heavy-rain.mp3`,
    defaultVolume: 0.2,
  },
  {
    id: "moderate-wind",
    name: "Moderate Wind",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/moderate-wind.mp3`,
    defaultVolume: 0.18,
  },
  {
    id: "newborn-baby-crying",
    name: "Newborn Baby Crying",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/newborn-baby-crying.mp3`,
    defaultVolume: 0.2,
  },
  {
    id: "office-with-alarm",
    name: "Office with Alarm",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/office-with-alarm.mp3`,
    defaultVolume: 0.7,
  },
  {
    id: "street-with-sirens",
    name: "Street with Sirens",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/street-with-sirens.mp3`,
    defaultVolume: 0.18,
  },
  {
    id: "construction-work",
    name: "Construction Work",
    previewUrl: `${S3_BACKGROUND_SOUND_BASE}/construction-work.mp3`,
    defaultVolume: 0.4,
  },
  {
    id: "backchanneling",
    name: "Backchanneling",
    defaultVolume: 1.0,
  },
]

export const DEFAULT_VOICE_TYPE = "cjVigY5qzO86Huf0OWal" // Eric voice - perfect for agentic use cases
export const DEFAULT_TEMPERATURE = 0.3
